/**
 * useNotifications Hook
 *
 * React Query hooks for the notification center.
 */

import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/core';
import { notify } from '@/shared/services/notificationService';
import type { NotificationFilters, UnreadCounts } from '../types';
import {
  getNotifications,
  getNotificationsPaginated,
  getUnreadCounts,
  markAsRead,
  markAllAsRead,
  reopenNotification,
  resolveNotification,
} from '../services/notificationCenterService';

const KEYS = {
  all: ['notifications'] as const,
  list: (filters?: NotificationFilters) =>
    ['notifications', 'list', filters] as const,
  unreadCount: ['notifications', 'unread-count'] as const,
};

export function useNotifications(filters?: NotificationFilters) {
  return useQuery({
    queryKey: KEYS.list(filters),
    queryFn: () => getNotifications(filters),
  });
}

export function useInfiniteNotifications(filters?: NotificationFilters) {
  return useInfiniteQuery({
    queryKey: KEYS.list(filters),
    queryFn: ({ pageParam = 1 }) => getNotificationsPaginated(pageParam, filters),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
  });
}

/** Unread-by-you per scope. Feeds the bell badge and its tab counts. */
export function useUnreadCounts() {
  return useQuery<UnreadCounts>({
    queryKey: KEYS.unreadCount,
    queryFn: getUnreadCounts,
    // Polling removed: invalidation via realtime WS (MODEL_TO_QUERY_KEYS)
  });
}

export function useMarkAsRead() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: markAsRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
    onError: () => {
      notify.error(t('notifications.markReadError', 'Failed to mark notification as read.'));
    },
  });
}

export function useMarkAllAsRead() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (filters?: NotificationFilters) => markAllAsRead(filters),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      notify.success(t('notifications.allMarkedRead', 'All notifications marked as read'));
    },
  });
}

/**
 * Close a shared entry for everyone.
 *
 * The success copy says so out loud: the person clicking needs to know this
 * is not the same as dismissing it from their own list, because it isn't.
 */
export function useResolveNotification() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: resolveNotification,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      notify.success(t('notifications.resolved', 'Resolved for everyone'));
    },
    onError: () => {
      notify.error(t('notifications.resolveError', 'Could not resolve this notification.'));
    },
  });
}

export function useReopenNotification() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: reopenNotification,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      notify.success(t('notifications.reopened', 'Back in the queue'));
    },
    onError: () => {
      notify.error(t('notifications.reopenError', 'Could not reopen this notification.'));
    },
  });
}
